import { useState } from "react";
import { Sparkles, ChevronRight, ExternalLink, CheckCircle2, X } from "lucide-react";
import { insightData } from "../../data/mock";
import { total, usd, type ExecutiveTransaction } from "../../data/executiveOverview";

const SEV_COLORS: Record<string, string> = {
  "severity--high":     "#ef4444",
  "severity--critical": "#dc2626",
  "severity--medium":   "#f59e0b",
  "severity--low":      "#10b981",
};

interface Props {
  onViewAll: () => void;
  rows?: ExecutiveTransaction[];
}

export function AIInsightsPanel({ onViewAll, rows }: Props) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [expanded,  setExpanded]  = useState<string | null>(null);

  const riskRows = rows?.filter((row) => row.Contract_Status !== "Contracted") || [];
  const tailRows = rows?.filter((row) => row.Tail_Spend_Flag === "Yes") || [];
  const maverickRows = rows?.filter((row) => row.Maverick_Spend_Flag === "Yes") || [];
  const duplicateRows = rows?.filter((row) => row.Duplicate_Flag === "Yes") || [];
  const confidence = (members: ExecutiveTransaction[]) => Math.round(members.reduce((sum, row) => sum + row.AI_Confidence_Score, 0) / (members.length || 1) * 100);
  const insights = rows ? [
    { ...insightData[0], title: `${new Set(riskRows.map((row) => row.Supplier_ID)).size} suppliers with non-contracted spend`, body: `${riskRows.length} transactions are expired or non-contracted.`, amount: usd(total(riskRows, "Spend_Amount_USD")), confidence: confidence(riskRows), actions: [] },
    { ...insightData[1], title: `Review ${tailRows.length} tail-spend transactions`, body: `${new Set(tailRows.map((row) => row.Supplier_ID)).size} suppliers contribute to tail spend.`, amount: usd(total(tailRows, "Spend_Amount_USD")), confidence: confidence(tailRows), actions: [] },
    { ...insightData[2], title: `${maverickRows.length} maverick purchases flagged`, body: `${usd(total(maverickRows, "Spend_Amount_USD"))} in maverick spend across the selected transactions.`, amount: usd(total(maverickRows, "Spend_Amount_USD")), confidence: confidence(maverickRows), actions: [] },
    { ...insightData[3], title: `${duplicateRows.length} possible duplicate transactions`, body: "Transactions flagged as potential duplicates in the source data.", amount: usd(total(duplicateRows, "Spend_Amount_USD")), confidence: confidence(duplicateRows), actions: [] },
  ].filter((item, index) => [riskRows, tailRows, maverickRows, duplicateRows][index].length > 0) : insightData;
  const visible = insights.filter((i) => !dismissed.has(i.id));

  return (
    <div className="insight-panel" role="region" aria-label="AI-generated insights">
      {/* Header */}
      <div className="insight-panel__header">
        <div>
          <div className="insight-panel__title">AI Insight Center</div>
          <div className="insight-panel__sub">{rows ? "Insights based on the selected procurement transactions." : "Generative insights synthesized from cleaned, classified, and enriched procurement data."}</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "flex-end" }}>
          <span className="insight-badge">
            <Sparkles size={4} aria-hidden="true" />
            {visible.length} Active Insights
          </span>
          <button
            type="button"
            className="view-all-link"
            onClick={onViewAll}
            style={{ display: "flex", alignItems: "center", gap: 4 }}
          >
            View All <ChevronRight size={12} />
          </button>
        </div>
      </div>

      {visible.length === 0 ? (
        <div style={{ textAlign: "center", padding: "32px 16px", color: "var(--text-muted)" }}>
          <CheckCircle2 size={32} color="var(--status-success)" style={{ margin: "0 auto 8px" }} />
          <div style={{ fontSize: 13, fontWeight: 600 }}>{rows ? "No insights for these filters" : "All insights addressed"}</div>
          <div style={{ fontSize: 11, marginTop: 4 }}>{rows ? "Adjust filters to view more transactions." : "Great work! No active insights right now."}</div>
        </div>
      ) : (
        <div className="insight-cards">
          {visible.map((ins) => {
            const isExpanded = expanded === ins.id;
            const color = SEV_COLORS[ins.sevCls] ?? "#6b3fa0";
            return (
              <div
                key={ins.id}
                className="insight-card"
                style={{ cursor: "pointer", transition: "box-shadow 0.18s" }}
                onClick={() => setExpanded(isExpanded ? null : ins.id)}
                onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.08)")}
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
              >
                <div className="insight-card__meta">
                  <span className="insight-card__category">{ins.category}</span>
                  <span className={`severity-badge ${ins.sevCls}`}>{ins.severity}</span>
                  <span className="insight-card__conf">{ins.confidence}% confidence</span>
                  <button
                    type="button"
                    aria-label={`Dismiss: ${ins.title}`}
                    onClick={(e) => { e.stopPropagation(); setDismissed((s) => new Set([...s, ins.id])); }}
                    style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 0, display: "flex" }}
                  >
                    <X size={13} />
                  </button>
                </div>
                <div className="insight-card__title">{ins.title}</div>
                <div className="insight-card__body">{isExpanded ? ins.body : `${ins.body.slice(0, 90)}…`}</div>
                <div className="insight-card__amount">{
                  typeof ins.amount === 'string' && ins.amount.includes(':')
                    ? ins.amount.split(':')[1].trim()
                    : (typeof ins.amount === 'string' ? ins.amount : `$${Number(ins.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}`)
                }</div>

                {isExpanded && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ background: "rgba(107,63,160,0.05)", borderRadius: 6, padding: "8px 10px", fontSize: 11, color: "var(--text-secondary)", marginBottom: 8, lineHeight: 1.5 }}>
                      <Sparkles size={10} style={{ display: "inline", marginRight: 4, color: "#6b3fa0" }} aria-hidden="true" />
                      {rows ? `Average classification confidence: ${ins.confidence}% for affected transactions.` : `AI Reasoning: ${ins.confidence}% confidence based on cross-dataset analysis of spend patterns, supplier contracts, and market benchmarks.`}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {ins.actions.map((a) => (
                        <button
                          key={a}
                          type="button"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            padding: "5px 10px", fontSize: 11, fontWeight: 600,
                            border: `1px solid ${color}`, color, borderRadius: 6,
                            background: "transparent", cursor: "pointer",
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = `${color}14`; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                        >
                          {a} <ExternalLink size={10} style={{ display: "inline" }} aria-hidden="true" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
